"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { X, ChevronLeft, ChevronRight, Grid, List, Image as ImageIcon, Filter, Star, Camera, Heart, Eye } from "lucide-react"
import axios from "axios"
import { useLanguage } from "../contexts/LanguageContext"
import Footer from "../components/Footer"

interface GalleryCategory {
    id: string
    name: string
    description: string
    is_active: boolean
    sort_order: number
    language_code: string
    created_at?: string
    updated_at?: string
}

interface GalleryItem {
    id: string
    title: string
    description: string
    image: string
    gallery_category_id: string
    gallery_category: GalleryCategory
    is_active: boolean
    sort_order: number
    language_code: string
}

const Gallery = () => {
    const { currentLanguage, t } = useLanguage()
    const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([])
    const [galleryCategories, setGalleryCategories] = useState<GalleryCategory[]>([])
    const [loading, setLoading] = useState(true)
    const [categoriesLoading, setCategoriesLoading] = useState(true)
    const [selectedCategory, setSelectedCategory] = useState("all")
    const [selectedImage, setSelectedImage] = useState<GalleryItem | null>(null)
    const [viewMode, setViewMode] = useState<'grid' | 'masonry'>('grid')
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    })

    // Récupérer les catégories depuis l'API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                console.log('Fetching gallery categories from API...', { currentLanguage })
                const response = await axios.get(`/api/v1/gallery-categories?lang=${currentLanguage}`)
                console.log('Gallery categories API response:', response.data)

                // Filtrer les catégories actives et les trier
                const activeCategories = response.data
                    .filter((cat: GalleryCategory) => cat.is_active)
                    .sort((a: GalleryCategory, b: GalleryCategory) => a.sort_order - b.sort_order)

                setGalleryCategories(activeCategories)
            } catch (error) {
                console.error('Failed to fetch gallery categories:', error)
                setGalleryCategories([])
            } finally {
                setCategoriesLoading(false)
            }
        }

        fetchCategories()
    }, [currentLanguage])

    // Récupérer les items de la galerie selon la catégorie sélectionnée
    useEffect(() => {
        const fetchGallery = async () => {
            try {
                setLoading(true)
                console.log('Fetching gallery from API...', { selectedCategory, currentLanguage })

                let url = '/api/v1/gallery'
                const params = new URLSearchParams()
                params.append('lang', currentLanguage)

                if (selectedCategory !== 'all') {
                    params.append('category_id', selectedCategory)
                }

                const fullUrl = `${url}?${params.toString()}`
                console.log('Gallery API URL:', fullUrl)
                const response = await axios.get(fullUrl)
                console.log('Gallery API response:', response.data)
                setGalleryItems(response.data || [])
            } catch (error) {
                console.error('Failed to fetch gallery:', error)
                setGalleryItems([])
            } finally {
                setLoading(false)
            }
        }

        // Ne récupérer la galerie que si les catégories sont chargées
        if (!categoriesLoading) {
            fetchGallery()
        }
    }, [selectedCategory, categoriesLoading, currentLanguage])

    // Pas besoin de filtrage côté client car l'API fait déjà le filtrage
    const filteredItems = galleryItems

    const nextImage = () => {
        if (selectedImage) {
            const currentIndex = filteredItems.findIndex((item) => item.id === selectedImage.id)
            const nextIndex = (currentIndex + 1) % filteredItems.length
            setSelectedImage(filteredItems[nextIndex])
        }
    }

    const prevImage = () => {
        if (selectedImage) {
            const currentIndex = filteredItems.findIndex((item) => item.id === selectedImage.id)
            const prevIndex = (currentIndex - 1 + filteredItems.length) % filteredItems.length
            setSelectedImage(filteredItems[prevIndex])
        }
    }

    if (categoriesLoading) {
        return (
            <div className="pt-16 lg:pt-20 min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center"
                >
                    <div className="relative w-20 h-20 mx-auto mb-6">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            className="w-20 h-20 border-4 border-primary-200 border-t-primary-600 rounded-full"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Camera className="w-8 h-8 text-primary-600" />
                        </div>
                    </div>
                    <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
                        {t('gallery.loadingCategories') || 'Loading gallery...'}
                    </p>
                </motion.div>
            </div>
        )
    }

    return (
        <div className="pt-16 lg:pt-20">
            {/* Enhanced Header */}
            <section className="relative section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent/5 rounded-full blur-2xl"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/3 rounded-full blur-2xl"></div>

                    {/* Pattern Overlay */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="w-full h-full" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2030/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                        }}></div>
                    </div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding text-center">
                    <motion.div
                        ref={ref}
                        initial={{ opacity: 0, y: 50 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        {/* Badge */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={inView ? { opacity: 1, scale: 1 } : {}}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="inline-flex items-center space-x-2 bg-primary/10 text-primary-400 px-6 py-3 rounded-full text-sm font-medium mb-8"
                        >
                            <Camera className="w-5 h-5" />
                            <span>{t('gallery.visualExperience')}</span>
                        </motion.div>

                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 text-white leading-tight">
                            {t('gallery.visualGallery')}
                        </h1>
                        <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light mb-10">
                            {t('gallery.subtitle') || 'Immerse yourself in a visual journey through our culinary artistry, ambiance, and unforgettable moments.'}
                        </p>

                        {/* Stats */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="flex justify-center items-center space-x-8 flex-wrap gap-4"
                        >
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <ImageIcon className="w-5 h-5 text-primary-400" />
                                <span className="text-sm text-gray-300 font-medium">{t('gallery.photos')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Star className="w-5 h-5 text-yellow-500" />
                                <span className="text-sm text-gray-300 font-medium">{t('gallery.highQuality')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Heart className="w-5 h-5 text-accent-500" />
                                <span className="text-sm text-gray-300 font-medium">{t('gallery.curatedCollection')}</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Enhanced Gallery Content */}
            <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-32 left-20 w-64 h-64 bg-primary/3 rounded-full blur-xl"></div>
                    <div className="absolute bottom-32 right-20 w-80 h-80 bg-accent/3 rounded-full blur-xl"></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    {/* Enhanced Controls */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="mb-16"
                    >
                        {/* Header with View Toggle */}
                        <div className="flex flex-col lg:flex-row items-center justify-between mb-8 gap-6">
                            <div className="text-center lg:text-left">
                                <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">
                                    {t('gallery.browseCollection')}
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300">
                                    {filteredItems.length} {filteredItems.length === 1 ? t('gallery.photo') : t('gallery.photosAvailable')}
                                </p>
                            </div>

                            {/* View Mode Toggle */}
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 rounded-2xl p-2 border border-gray-200 dark:border-gray-700">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-3 rounded-xl transition-all duration-300 ${viewMode === 'grid'
                                            ? 'bg-primary-500 text-white shadow-lg'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-primary-500'
                                            }`}
                                    >
                                        <Grid className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('masonry')}
                                        className={`p-3 rounded-xl transition-all duration-300 ${viewMode === 'masonry'
                                            ? 'bg-primary-500 text-white shadow-lg'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-primary-500'
                                            }`}
                                    >
                                        <List className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Category Filter */}
                        <div className="flex items-center justify-center mb-8">
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 rounded-2xl p-2 border border-gray-200 dark:border-gray-700 shadow-lg">
                                <Filter className="w-5 h-5 text-gray-500 ml-4" />
                                <div className="flex flex-wrap gap-2 py-2">
                                    {/* All Photos Button */}
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => setSelectedCategory('all')}
                                        className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${selectedCategory === 'all'
                                            ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg"
                                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            }`}
                                    >
                                        {t('gallery.allPhotos')}
                                    </motion.button>

                                    {/* Dynamic Category Buttons */}
                                    {galleryCategories.map((category, index) => (
                                        <motion.button
                                            key={category.id}
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.3, delay: index * 0.1 }}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => setSelectedCategory(category.id)}
                                            className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${selectedCategory === category.id
                                                ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg"
                                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                }`}
                                            title={category.description}
                                        >
                                            {category.name}
                                        </motion.button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Enhanced Loading State */}
                    {loading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-16"
                        >
                            <div className="relative w-16 h-16 mx-auto mb-6">
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                    className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full"
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <ImageIcon className="w-6 h-6 text-primary-600" />
                                </div>
                            </div>
                            <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
                                {t('gallery.loadingItems') || 'Loading beautiful photos...'}
                            </p>
                        </motion.div>
                    )}

                    {/* Enhanced Gallery Grid */}
                    {!loading && (
                        <div className={`gap-8 ${viewMode === 'grid'
                            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                            : 'columns-1 md:columns-2 lg:columns-3 space-y-8'
                            }`}>
                            {filteredItems.map((item, index) => (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className={`group cursor-pointer ${viewMode === 'masonry' ? 'break-inside-avoid mb-8' : ''}`}
                                    onClick={() => setSelectedImage(item)}
                                >
                                    <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary-500/20 hover:-translate-y-2">
                                        <div className={`overflow-hidden ${viewMode === 'grid' ? 'aspect-[4/3]' : 'aspect-auto'}`}>
                                            <img
                                                src={item.image || "/placeholder.svg"}
                                                alt={item.title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                            />
                                        </div>

                                        {/* Enhanced Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end justify-center p-6">
                                            <div className="text-white text-center transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                                                <h3 className="text-lg font-serif font-bold mb-2">{item.title}</h3>
                                                <p className="text-xs text-white/90 leading-relaxed mb-3">{item.description}</p>

                                                {/* View Button */}
                                                <motion.div
                                                    whileHover={{ scale: 1.05 }}
                                                    className="inline-flex items-center space-x-2 bg-white/20 px-3 py-1.5 rounded-full text-xs font-medium"
                                                >
                                                    <Eye className="w-3 h-3" />
                                                    <span>{t('gallery.viewFullSize')}</span>
                                                </motion.div>
                                            </div>
                                        </div>

                                        {/* Enhanced Category Badge */}
                                        <div className="absolute top-4 left-4">
                                            <div className="flex items-center space-x-2 bg-white/90 px-3 py-2 rounded-full shadow-lg">
                                                <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                                                <span className="text-xs font-semibold text-gray-800 capitalize">
                                                    {item.gallery_category?.name || t('gallery.gallery')}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Hover Glow Effect */}
                                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary-500/10 to-accent-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {/* Enhanced Empty State */}
                    {!loading && filteredItems.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center py-24"
                        >
                            <div className="max-w-lg mx-auto">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ duration: 0.5, delay: 0.2 }}
                                    className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg"
                                >
                                    <Camera className="w-16 h-16 text-gray-400 dark:text-gray-500" />
                                </motion.div>
                                <h3 className="text-xl font-serif font-bold text-gray-900 dark:text-white mb-3">
                                    {t('gallery.noPhotosFound')}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 text-base leading-relaxed mb-4">
                                    {t('gallery.noPhotos') || 'No photos are available in this category at the moment.'}
                                </p>
                                <p className="text-gray-500 dark:text-gray-500 text-sm mb-6">
                                    {t('gallery.tryDifferentCategory') || 'Try selecting a different category or check back later for new content.'}
                                </p>

                                {/* Back to All Photos Button */}
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => setSelectedCategory('all')}
                                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold px-6 py-3 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl"
                                >
                                    <ImageIcon className="w-5 h-5" />
                                    <span>{t('gallery.viewAllPhotos')}</span>
                                </motion.button>
                            </div>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* Enhanced Lightbox Modal */}
            {selectedImage && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4"
                >
                    <div className="relative max-w-6xl w-full max-h-[90vh]">
                        {/* Enhanced Close Button */}
                        <motion.button
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            whileHover={{ scale: 1.1 }}
                            onClick={() => setSelectedImage(null)}
                            className="absolute top-6 right-6 z-10 w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 border border-white/20 shadow-lg"
                        >
                            <X className="w-6 h-6" />
                        </motion.button>

                        {/* Enhanced Navigation Buttons */}
                        <motion.button
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            whileHover={{ scale: 1.05 }}
                            onClick={prevImage}
                            className="absolute left-6 top-1/2 transform -translate-y-1/2 z-10 w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 border border-white/20 shadow-lg"
                        >
                            <ChevronLeft className="w-8 h-8" />
                        </motion.button>
                        <motion.button
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            whileHover={{ scale: 1.05 }}
                            onClick={nextImage}
                            className="absolute right-6 top-1/2 transform -translate-y-1/2 z-10 w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 border border-white/20 shadow-lg"
                        >
                            <ChevronRight className="w-8 h-8" />
                        </motion.button>

                        {/* Enhanced Image Container */}
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="relative rounded-3xl overflow-hidden shadow-2xl"
                        >
                            <img
                                src={selectedImage.image || "/placeholder.svg"}
                                alt={selectedImage.title}
                                className="w-full h-full object-contain max-h-[80vh]"
                            />
                        </motion.div>

                        {/* Enhanced Image Info */}
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.2 }}
                            className="absolute bottom-6 left-6 right-6 bg-black/60 rounded-3xl p-8 text-white border border-white/10 shadow-2xl"
                        >
                            <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-3">
                                        <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                                        <span className="text-sm font-medium text-primary-400 uppercase tracking-wider">
                                            {selectedImage.gallery_category?.name || t('gallery.gallery')}
                                        </span>
                                    </div>
                                    <h3 className="text-2xl font-serif font-bold mb-2">{selectedImage.title}</h3>
                                    <p className="text-white/90 leading-relaxed text-base">{selectedImage.description}</p>
                                </div>

                                {/* Image Counter */}
                                <div className="flex flex-col items-end space-y-2">
                                    <div className="bg-white/10 px-4 py-2 rounded-full">
                                        <span className="text-sm font-medium">
                                            {filteredItems.findIndex(item => item.id === selectedImage.id) + 1} of {filteredItems.length}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2 text-sm text-white/70">
                                        <Eye className="w-4 h-4" />
                                        <span>{t('gallery.fullResolution')}</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            )}

            <Footer />
        </div>
    )
}

export default Gallery
